<template>
  <div class="api-test-container">
    <!-- 接口地址配置 -->
    <a-card style="margin-bottom: 16px">
      <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
        <a-form-item label="接口地址">
          <a-input
            v-model:value="baseUrl"
            placeholder="/api"
            style="width: 300px; margin-right: 8px"
          />
          <a-button @click="saveBaseUrl">保存</a-button>
          <span style="margin-left: 8px; color: #999; font-size: 12px">
            完整URL: {{ baseUrl }}/ssz_ai
          </span>
        </a-form-item>
      </a-form>
    </a-card>

    <a-tabs v-model:activeKey="activeMethod" @change="handleMethodChange">
      <!-- POST 测试 -->
      <a-tab-pane key="POST" tab="POST 测试" v-if="apiConfig?.methods?.post">
        <div class="test-section">
          <div class="test-header">
            <span class="test-title">请求数据</span>
            <a-space>
              <a-button size="small" @click="loadTemplate('create')">
                加载创建模板
              </a-button>
              <a-button size="small" @click="loadTemplate('continue')">
                加载继续执行模板
              </a-button>
              <a-button size="small" @click="formatJson">格式化</a-button>
              <a-button size="small" @click="clearData">清空</a-button>
            </a-space>
          </div>
          <CodeEditor
            v-model="postRequestData"
            language="json"
            :height="400"
            @change="handleRequestDataChange"
          />
          <div v-if="jsonError" class="error-message">
            <a-alert :message="jsonError" type="error" show-icon />
          </div>
        </div>

        <div class="test-actions">
          <a-button type="primary" @click="handleTestPost" :loading="testing">
            发送请求
          </a-button>
        </div>
      </a-tab-pane>

      <!-- GET 测试 -->
      <a-tab-pane key="GET" tab="GET 测试" v-if="apiConfig?.methods?.get">
        <div class="test-section">
          <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <a-form-item label="task_id" required>
              <a-input
                v-model:value="getTaskId"
                placeholder="请输入任务ID"
                style="width: 100%"
              />
            </a-form-item>
          </a-form>
        </div>

        <div class="test-actions">
          <a-button type="primary" @click="handleTestGet" :loading="testing">
            发送请求
          </a-button>
        </div>
      </a-tab-pane>
    </a-tabs>

    <!-- 响应结果 -->
    <a-divider v-if="responseData">响应结果</a-divider>
    <div v-if="responseData" class="response-section">
      <div class="response-header">
        <a-space>
          <a-tag :color="responseStatus >= 200 && responseStatus < 300 ? 'green' : 'red'">
            状态码: {{ responseStatus }}
          </a-tag>
          <span v-if="responseTime">响应时间: {{ responseTime }}ms</span>
        </a-space>
        <a-space>
          <a-button size="small" @click="formatResponse">格式化响应</a-button>
          <a-button size="small" @click="copyResponse">复制响应</a-button>
        </a-space>
      </div>
      <CodeEditor
        :model-value="formattedResponse"
        language="json"
        :height="400"
        :read-only="true"
      />
    </div>

    <!-- 错误信息 -->
    <a-divider v-if="errorInfo">错误信息</a-divider>
    <div v-if="errorInfo" class="error-section">
      <a-alert
        :message="errorInfo.message"
        :description="errorInfo.description"
        type="error"
        show-icon
        style="margin-bottom: 16px"
      />
      <div v-if="errorInfo.details">
        <a-typography-title :level="5">错误详情</a-typography-title>
        <CodeEditor
          :model-value="formatJson(errorInfo.details)"
          language="json"
          :height="200"
          :read-only="true"
        />
      </div>
      <div v-if="errorInfo.errorCode" style="margin-top: 16px">
        <a-typography-title :level="5">错误码说明</a-typography-title>
        <a-descriptions :column="1" bordered size="small">
          <a-descriptions-item label="错误码">
            <a-tag color="red">{{ errorInfo.errorCode.code }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="错误信息">
            {{ errorInfo.errorCode.message }}
          </a-descriptions-item>
          <a-descriptions-item label="说明">
            {{ errorInfo.errorCode.description }}
          </a-descriptions-item>
        </a-descriptions>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import CodeEditor from './CodeEditor.vue';
import { testSszAiPost, testSszAiGet } from '../api/apiTest';

export default {
  name: 'ApiTest',
  components: {
    CodeEditor
  },
  props: {
    apiConfig: {
      type: Object,
      required: true
    },
    baseUrl: {
      type: String,
      default: '/api'
    }
  },
  emits: ['close', 'update:baseUrl'],
  setup(props, { emit }) {
    const activeMethod = ref('POST');
    const baseUrl = ref(props.baseUrl || '/api');
    const postRequestData = ref('');
    const getTaskId = ref('');
    const testing = ref(false);
    const responseData = ref(null);
    const responseStatus = ref(null);
    const responseTime = ref(null);
    const errorInfo = ref(null);
    const jsonError = ref('');

    // 保存 baseUrl
    const saveBaseUrl = () => {
      if (props.apiConfig?.id) {
        localStorage.setItem(`api_test_base_url_${props.apiConfig.id}`, baseUrl.value);
        emit('update:baseUrl', baseUrl.value);
        message.success('接口地址已保存');
      }
    };

    // 加载保存的测试数据
    const loadSavedTestData = () => {
      if (props.apiConfig?.id) {
        // 加载 POST 测试数据
        const savedPostData = localStorage.getItem(`api_test_post_data_${props.apiConfig.id}`);
        if (savedPostData) {
          postRequestData.value = savedPostData;
        }
        
        // 加载 GET 测试数据
        const savedTaskId = localStorage.getItem(`api_test_get_task_id_${props.apiConfig.id}`);
        if (savedTaskId) {
          getTaskId.value = savedTaskId;
        }
      }
    };

    // 保存 POST 测试数据
    const savePostTestData = () => {
      if (props.apiConfig?.id && postRequestData.value) {
        localStorage.setItem(`api_test_post_data_${props.apiConfig.id}`, postRequestData.value);
        message.success('测试数据已保存');
      }
    };

    // 保存 GET 测试数据
    const saveGetTestData = () => {
      if (props.apiConfig?.id && getTaskId.value) {
        localStorage.setItem(`api_test_get_task_id_${props.apiConfig.id}`, getTaskId.value);
        message.success('测试数据已保存');
      }
    };

    // 初始化请求数据
    const initRequestData = () => {
      if (props.apiConfig?.methods?.post?.requestExample?.create) {
        postRequestData.value = formatJson(
          props.apiConfig.methods.post.requestExample.create.data
        );
      }
    };

    // 格式化JSON
    const formatJson = (obj) => {
      if (typeof obj === 'string') {
        try {
          const parsed = JSON.parse(obj);
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          return obj;
        }
      }
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    };

    // 加载模板
    const loadTemplate = (type) => {
      if (props.apiConfig?.methods?.post?.requestExample?.[type]) {
        const example = props.apiConfig.methods.post.requestExample[type];
        // 优先使用messageContent（如果存在），否则解析message字段
        if (example.messageContent) {
          // 直接显示message字段内的内容，方便用户编辑
          postRequestData.value = formatJson(example.messageContent);
        } else if (example.data?.message && typeof example.data.message === 'string') {
          try {
            const messageContent = JSON.parse(example.data.message);
            // 显示message字段内的内容，方便用户编辑
            postRequestData.value = formatJson(messageContent);
          } catch (e) {
            // 如果解析失败，直接显示原始数据
            postRequestData.value = formatJson(example.data);
          }
        } else {
          postRequestData.value = formatJson(example.data);
        }
        jsonError.value = '';
      }
    };

    // 清空数据
    const clearData = () => {
      postRequestData.value = '';
      jsonError.value = '';
    };

    // 格式化JSON编辑器内容
    const formatJsonEditor = () => {
      try {
        const parsed = JSON.parse(postRequestData.value);
        postRequestData.value = formatJson(parsed);
        jsonError.value = '';
      } catch (e) {
        jsonError.value = `JSON格式错误: ${e.message}`;
      }
    };

    // 请求数据变化
    const handleRequestDataChange = () => {
      jsonError.value = '';
      try {
        JSON.parse(postRequestData.value);
        // 自动保存
        savePostTestData();
      } catch (e) {
        // 不立即显示错误，只在发送时验证
      }
    };

    // task_id 变化时自动保存
    watch(getTaskId, (newVal) => {
      if (newVal) {
        saveGetTestData();
      }
    });

    // 方法切换
    const handleMethodChange = () => {
      responseData.value = null;
      errorInfo.value = null;
    };

    // 测试POST
    const handleTestPost = async () => {
      // 验证JSON格式
      let requestData;
      try {
        requestData = JSON.parse(postRequestData.value);
      } catch (e) {
        jsonError.value = `JSON格式错误: ${e.message}`;
        message.error('请求数据格式错误，请检查JSON格式');
        return;
      }

      // 处理message字段：如果请求数据中没有message字段，自动包装
      // 新接口格式要求：{"message": "JSON字符串"}
      if (!requestData.message) {
        // 如果请求数据是message字段内的内容，自动包装成message字段
        requestData = {
          message: JSON.stringify(requestData)
        };
      } else if (typeof requestData.message === 'object') {
        // 如果message是对象，转换为JSON字符串
        requestData = {
          message: JSON.stringify(requestData.message)
        };
      }
      // 如果message已经是字符串，直接使用

      testing.value = true;
      responseData.value = null;
      errorInfo.value = null;
      responseStatus.value = null;
      responseTime.value = null;

      const startTime = Date.now();

      try {
        const response = await testSszAiPost(requestData, baseUrl.value);
        responseTime.value = Date.now() - startTime;
        // axios 响应对象结构：response.data 是实际数据，response.status 是状态码
        responseStatus.value = response.status || 200;
        // 如果响应数据是对象且包含 code，说明是业务响应
        if (response.data && typeof response.data === 'object') {
          responseData.value = response.data;
        } else {
          responseData.value = response.data;
        }
        errorInfo.value = null;
        message.success('请求成功');
      } catch (error) {
        responseTime.value = Date.now() - startTime;
        
        // 处理错误响应
        if (error.response) {
          responseStatus.value = error.response.status;
          responseData.value = error.response.data;
          
          // 查找错误码说明
          const errorCode = findErrorCode(error.response.status, error.response.data);
          errorInfo.value = {
            message: error.response.data?.message || '请求失败',
            description: error.response.data?.message || `HTTP ${error.response.status}`,
            details: error.response.data,
            errorCode: errorCode
          };
        } else {
          responseStatus.value = null;
          responseData.value = null;
          errorInfo.value = {
            message: '网络错误',
            description: error.message || '无法连接到服务器',
            details: { error: error.message }
          };
        }
        message.error('请求失败');
      } finally {
        testing.value = false;
      }
    };

    // 测试GET
    const handleTestGet = async () => {
      if (!getTaskId.value) {
        message.warning('请输入 task_id');
        return;
      }

      testing.value = true;
      responseData.value = null;
      errorInfo.value = null;
      responseStatus.value = null;
      responseTime.value = null;

      const startTime = Date.now();

      try {
        const response = await testSszAiGet(getTaskId.value, baseUrl.value);
        responseTime.value = Date.now() - startTime;
        // axios 响应对象结构：response.data 是实际数据，response.status 是状态码
        responseStatus.value = response.status || 200;
        // 如果响应数据是对象且包含 code，说明是业务响应
        if (response.data && typeof response.data === 'object') {
          responseData.value = response.data;
        } else {
          responseData.value = response.data;
        }
        errorInfo.value = null;
        message.success('请求成功');
      } catch (error) {
        responseTime.value = Date.now() - startTime;
        
        if (error.response) {
          responseStatus.value = error.response.status;
          responseData.value = error.response.data;
          
          const errorCode = findErrorCode(error.response.status, error.response.data);
          errorInfo.value = {
            message: error.response.data?.message || '请求失败',
            description: error.response.data?.message || `HTTP ${error.response.status}`,
            details: error.response.data,
            errorCode: errorCode
          };
        } else {
          responseStatus.value = null;
          responseData.value = null;
          errorInfo.value = {
            message: '网络错误',
            description: error.message || '无法连接到服务器',
            details: { error: error.message }
          };
        }
        message.error('请求失败');
      } finally {
        testing.value = false;
      }
    };

    // 查找错误码说明
    const findErrorCode = (status, responseData) => {
      const method = activeMethod.value.toLowerCase();
      const methodConfig = props.apiConfig?.methods?.[method];
      if (!methodConfig?.errorCodes) return null;

      // 先按状态码查找
      let errorCode = methodConfig.errorCodes.find(ec => ec.code === status);
      
      // 如果没找到，尝试从响应消息中匹配
      if (!errorCode && responseData?.message) {
        errorCode = methodConfig.errorCodes.find(ec => 
          ec.examples?.some(example => responseData.message.includes(example))
        );
      }

      return errorCode || null;
    };

    // 格式化响应
    const formattedResponse = computed(() => {
      if (!responseData.value) return '';
      return formatJson(responseData.value);
    });

    // 格式化响应按钮
    const formatResponse = () => {
      // 响应已经是格式化的，这里可以触发重新渲染
    };

    // 复制响应
    const copyResponse = async () => {
      try {
        await navigator.clipboard.writeText(formattedResponse.value);
        message.success('已复制到剪贴板');
      } catch (e) {
        message.error('复制失败');
      }
    };

    // 初始化
    onMounted(() => {
      initRequestData();
      loadSavedTestData();
    });

    return {
      activeMethod,
      baseUrl,
      postRequestData,
      getTaskId,
      testing,
      responseData,
      responseStatus,
      responseTime,
      errorInfo,
      jsonError,
      formattedResponse,
      saveBaseUrl,
      loadTemplate,
      clearData,
      formatJson: formatJsonEditor,
      handleRequestDataChange,
      handleMethodChange,
      handleTestPost,
      handleTestGet,
      formatResponse,
      copyResponse
    };
  }
};
</script>

<style scoped>
.api-test-container {
  padding: 16px 0;
}

.test-section {
  margin-bottom: 16px;
}

.test-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.test-title {
  font-weight: 500;
  font-size: 14px;
}

.test-actions {
  margin-top: 16px;
  text-align: right;
}

.error-message {
  margin-top: 8px;
}

.response-section {
  margin-top: 16px;
}

.response-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.error-section {
  margin-top: 16px;
}
</style>

